import React, { useEffect, useState, useRef } from 'react';
import { Play, Clock, Calendar, ArrowRight, BookOpen, Brain, Target, Lightbulb, User, Settings, Pause, Volume2, VolumeX, Maximize, RotateCcw, RotateCw, Video, MessageSquare, Send, ChevronRight, Download, FileText } from 'lucide-react';
import { GuidanceVideosGallery } from '../components/GuidanceVideosGallery';
import { GuidanceNotesLibrary } from '../components/GuidanceNotesLibrary';
import { ProgramRequestModal } from '../components/ProgramRequestModal';
import { SupportMessageModal } from '../components/SupportMessageModal';
import { useToast } from '../components/Toast';
import { VideoPlayer } from '../components/VideoPlayer';
// Interface for the weekly program data
interface AktifHafta {
  videoUrl: string;
  videoBasligi: string;
  videoAciklamasi: string;
  gorselUrl: string;
  pdfUrl: string;
}
export function Guidance() {
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [showNotesLibrary, setShowNotesLibrary] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showProgramRequestModal, setShowProgramRequestModal] = useState(false);
  const [showSupportMessageModal, setShowSupportMessageModal] = useState(false);
  const videoPlayerRef = useRef<HTMLDivElement>(null);
  const settingsRef = useRef<HTMLDivElement>(null);
  const {
    showToast,
    ToastContainer
  } = useToast();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Sample data for the current week's guidance program
  const aktifHafta: AktifHafta = {
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    videoBasligi: 'Ekim Ayı 1. Hafta',
    videoAciklamasi: '',
    gorselUrl: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hAg3VKT4sKX3Ar8RQNmnBW/ders_progrma%C4%B1.jpg',
    pdfUrl: '#program-pdf'
  };
  // Guidance videos for horizontal gallery
  const guidanceVideos = [{
    id: 1,
    title: 'Verimli Ders Çalışma Teknikleri',
    thumbnail: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=300&h=180&fit=crop',
    duration: '08:25',
    category: 'Verimli Ders Çalışma'
  }, {
    id: 2,
    title: 'Motivasyon Artırma Yöntemleri',
    thumbnail: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=300&h=180&fit=crop',
    duration: '12:17',
    category: 'Motivasyon'
  }, {
    id: 3,
    title: 'Hedef Belirleme ve Planlama',
    thumbnail: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=300&h=180&fit=crop',
    duration: '10:05',
    category: 'Hedef Belirleme'
  }, {
    id: 4,
    title: 'Zaman Yönetimi Stratejileri',
    thumbnail: 'https://images.unsplash.com/photo-1483058712412-4245e9b90334?w=300&h=180&fit=crop',
    duration: '14:30',
    category: 'Zaman Yönetimi'
  }, {
    id: 5,
    title: 'Stres Yönetimi ve Rahatlama Teknikleri',
    thumbnail: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=300&h=180&fit=crop',
    duration: '09:45',
    category: 'Sınav Kaygısı'
  }];
  // Past guidance program videos
  const rehberlikVideolari = [{
    id: 'hafta-5',
    title: 'Ekim Ayı 1. Hafta',
    category: 'Rehberlik',
    thumbnail: 'https://images.unsplash.com/photo-1483058712412-4245e9b90334?w=300&h=180&fit=crop',
    duration: '14:30'
  }, {
    id: 'hafta-4',
    title: 'Eylül Ayı 4. Hafta',
    category: 'Rehberlik',
    thumbnail: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=300&h=180&fit=crop',
    duration: '10:05'
  }, {
    id: 'hafta-3',
    title: 'Eylül Ayı 3. Hafta',
    category: 'Rehberlik',
    thumbnail: 'https://images.unsplash.com/photo-1552581234-26160f608093?w=300&h=180&fit=crop',
    duration: '12:17'
  }, {
    id: 'hafta-2',
    title: 'Eylül Ayı 2. Hafta',
    category: 'Rehberlik',
    thumbnail: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=300&h=180&fit=crop',
    duration: '08:25'
  }, {
    id: 'hafta-1',
    title: 'Eylül Ayı 1. Hafta',
    category: 'Rehberlik',
    thumbnail: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=300&h=180&fit=crop',
    duration: '09:45'
  }];
  // Combined videos for filtering
  const allVideos = [...guidanceVideos, ...rehberlikVideolari];
  // Guidance notes preview
  const guidanceNotes = [{
    id: 1,
    title: 'Sık Sorulan Sorular',
    description: 'Öğrencilerin rehberlik hakkında en çok merak ettiği konular',
    icon: MessageSquare
  }, {
    id: 2,
    title: 'Öğrenme Stilleri',
    description: 'Kendi öğrenme stilinizi keşfedin ve çalışma stratejinizi belirleyin',
    icon: Brain
  }, {
    id: 3,
    title: 'Hedef Belirleme Rehberi',
    description: 'SMART hedefler oluşturmanın adımları ve önemi',
    icon: Target
  }, {
    id: 4,
    title: 'Yaratıcı Düşünme Teknikleri',
    description: 'Problem çözme ve yaratıcılığınızı geliştirmenin yolları',
    icon: Lightbulb
  }];
  // Categories for filtering
  const categories = ['Tümü', 'Rehberlik', 'Verimli Ders Çalışma', 'Motivasyon', 'Sınav Kaygısı', 'Zaman Yönetimi', 'Hedef Belirleme'];
  // Filter videos based on selected category
  const getFilteredVideos = () => {
    if (!activeCategory || activeCategory === 'Tümü') {
      return allVideos;
    }
    return allVideos.filter(video => video.category === activeCategory);
  };
  // Parse duration string (e.g., "12:34") to seconds
  const parseTimeToSeconds = (timeString: string) => {
    const [mins, secs] = timeString.split(':').map(Number);
    return mins * 60 + secs;
  };
  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!videoPlayerRef.current) return;
    if (!isFullscreen) {
      if (videoPlayerRef.current.requestFullscreen) {
        videoPlayerRef.current.requestFullscreen();
      } else if ((videoPlayerRef.current as any).webkitRequestFullscreen) {
        ;
        (videoPlayerRef.current as any).webkitRequestFullscreen();
      } else if ((videoPlayerRef.current as any).msRequestFullscreen) {
        ;
        (videoPlayerRef.current as any).msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        ;
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        ;
        (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };
  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);
  // Close settings menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettingsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Simulate video progress
  useEffect(() => {
    if (isPlaying) {
      const totalDurationInSeconds = parseTimeToSeconds(aktifHafta.videoAciklamasi);
      const timerInterval = 100 / playbackSpeed; // Faster playback = faster timer
      const timer = setInterval(() => {
        setProgress(prev => {
          // Calculate new progress percentage, adjusted for playback speed
          const newProgress = prev + 100 / (totalDurationInSeconds * 10) * playbackSpeed;
          // Update current time display
          const currentTimeInSeconds = newProgress / 100 * totalDurationInSeconds;
          setCurrentTime(formatTime(currentTimeInSeconds));
          if (newProgress >= 100) {
            clearInterval(timer);
            setIsPlaying(false);
            return 100;
          }
          return newProgress;
        });
      }, timerInterval);
      return () => clearInterval(timer);
    }
  }, [isPlaying, aktifHafta.videoAciklamasi, playbackSpeed]);
  // Handle seeking forward or backward
  const handleSeek = (seconds: number) => {
    const totalDurationInSeconds = parseTimeToSeconds(aktifHafta.videoAciklamasi);
    const currentTimeInSeconds = progress / 100 * totalDurationInSeconds;
    // Calculate new time in seconds after seeking
    let newTimeInSeconds = currentTimeInSeconds + seconds;
    // Ensure new time is within bounds
    newTimeInSeconds = Math.max(0, Math.min(newTimeInSeconds, totalDurationInSeconds));
    // Calculate new progress percentage
    const newProgress = newTimeInSeconds / totalDurationInSeconds * 100;
    // Update progress and current time display
    setProgress(newProgress);
    setCurrentTime(formatTime(newTimeInSeconds));
  };
  // Handle modal submit actions
  const handleProgramRequestSubmit = (data: {
    details: string;
  }) => {
    // Handle program request submission
    setShowProgramRequestModal(false);
    showToast('Program talebiniz başarıyla gönderildi!', 'success');
  };
  const handleSupportMessageSubmit = (data: {
    subject: string;
    message: string;
  }) => {
    // Handle support message submission
    setShowSupportMessageModal(false);
    showToast('Mesajınız başarıyla gönderildi!', 'success');
  };
  if (showAllVideos) {
    return <GuidanceVideosGallery onClose={() => setShowAllVideos(false)} data-id="element-1983" />;
  }
  if (showNotesLibrary) {
    return <GuidanceNotesLibrary onClose={() => setShowNotesLibrary(false)} data-id="element-1984" />;
  }
  return <div className="space-y-6" data-id="element-1985">
      <h1 className="text-2xl font-bold" data-id="element-1986">Rehberlik</h1>
      {/* Weekly Guidance Program Section - With Working Video Player */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1987">
        <div className="relative bg-black/90 rounded-xl p-4" data-id="element-1988">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6" data-id="element-1989">
            <div className="lg:w-2/3" data-id="element-1990">
              <h2 className="text-xl font-semibold mb-3" data-id="element-1991">
                Haftanın Rehberlik Programı
              </h2>
              <div className="aspect-video bg-black rounded-lg overflow-hidden relative" data-id="element-1992">
                <VideoPlayer videoUrl={aktifHafta.videoUrl} thumbnailUrl="https://uploadthingy.s3.us-west-1.amazonaws.com/dYfYjrrTM3QZRb3VtxkFdT/Image_fx_%2811%29.jpg" enableFullscreen={true} autoPlay={false} data-id="element-1993" />
              </div>
              <h3 className="text-xl font-semibold mt-3" data-id="element-1994">
                {aktifHafta.videoBasligi}
              </h3>
            </div>
            {/* RIGHT SIDE - MATERIALS (Narrower Column) */}
            <div className="lg:w-1/3 space-y-4" data-id="element-1995">
              <div data-id="element-1996">
                <h4 className="text-lg font-semibold mb-2" data-id="element-1997">Program Özeti</h4>
                <div className="rounded-lg overflow-hidden border border-gray-700" data-id="element-1998">
                  <img src={aktifHafta.gorselUrl} alt="Haftalık Program Özeti" className="w-full h-auto" data-id="element-1999" />
                </div>
              </div>
              <div data-id="element-2000">
                <h4 className="text-lg font-semibold mb-2" data-id="element-2001">Program Dosyası</h4>
                <p className="text-sm text-gray-400 mb-2" data-id="element-2002">
                  Programın tamamını PDF olarak indirip çıktısını alabilirsin.
                </p>
                <a href={aktifHafta.pdfUrl} download className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-2003">
                  <Download className="w-5 h-5" data-id="element-2004" />
                  PDF Olarak İndir
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Service Cards Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2005">
        {/* Individual Program Request Card */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2006">
          <div className="relative bg-black/90 rounded-xl p-4 h-full" data-id="element-2007">
            <div className="flex flex-col h-full" data-id="element-2008">
              <div className="flex items-center gap-3 mb-3" data-id="element-2009">
                <div className="p-2.5 bg-purple-500/20 text-purple-400 rounded-lg" data-id="element-2010">
                  <Calendar className="w-5 h-5" data-id="element-2011" />
                </div>
                <h2 className="text-xl font-semibold" data-id="element-2012">
                  Bireysel Program Talebi
                </h2>
              </div>
              <p className="text-gray-300 mb-3" data-id="element-2013">
                Akademik hedeflerinize ulaşmak için size özel hazırlanmış bir
                çalışma programı talep edin. Rehberlik uzmanlarımız sizinle
                iletişime geçecektir.
              </p>
              <div className="mt-auto" data-id="element-2014">
                <button onClick={() => setShowProgramRequestModal(true)} className="w-full py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-2015">
                  <Calendar className="w-5 h-5" data-id="element-2016" />
                  Program Talebi Oluştur
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Support Message Card */}
        <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2017">
          <div className="relative bg-black/90 rounded-xl p-4 h-full" data-id="element-2018">
            <div className="flex flex-col h-full" data-id="element-2019">
              <div className="flex items-center gap-3 mb-3" data-id="element-2020">
                <div className="p-2.5 bg-purple-500/20 text-purple-400 rounded-lg" data-id="element-2021">
                  <MessageSquare className="w-5 h-5" data-id="element-2022" />
                </div>
                <h2 className="text-xl font-semibold" data-id="element-2023">Destek Mesajı Gönder</h2>
              </div>
              <p className="text-gray-300 mb-3" data-id="element-2024">
                Akademik veya kişisel konularda desteğe mi ihtiyacınız var?
                Rehberlik uzmanlarımıza mesaj gönderin, en kısa sürede size
                dönüş yapacaklardır.
              </p>
              <div className="mt-auto" data-id="element-2025">
                <button onClick={() => setShowSupportMessageModal(true)} className="w-full py-2.5 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors flex items-center justify-center gap-2" data-id="element-2026">
                  <Send className="w-5 h-5" data-id="element-2027" />
                  Mesaj Gönder
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Videos Gallery Section with Updated Category Filters */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2028">
        <div className="relative bg-black/90 rounded-xl p-4" data-id="element-2029">
          <div className="flex justify-between items-center mb-3" data-id="element-2030">
            <h2 className="text-xl font-semibold" data-id="element-2031">Rehberlik Videoları</h2>
            <button onClick={() => setShowAllVideos(true)} className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1" data-id="element-2032">
              Tümünü Gör
              <ArrowRight className="w-4 h-4" data-id="element-2033" />
            </button>
          </div>
          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2" data-id="element-2034">
            {categories.map(category => <button key={category} onClick={() => setActiveCategory(category === 'Tümü' ? null : category)} className={`px-3 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap ${category === 'Tümü' && activeCategory === null || category === activeCategory ? 'bg-purple-500 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`} data-id="element-2035">
                {category}
              </button>)}
          </div>
          {/* Video Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-2036">
            {getFilteredVideos().map(video => <div key={video.id} className="flex-shrink-0 hover:scale-[1.03] transition-transform duration-200 cursor-pointer" onClick={() => setShowAllVideos(true)} data-id="element-2037">
                <div className="relative rounded-lg overflow-hidden" data-id="element-2038">
                  <img src={video.thumbnail} alt={video.title} className="w-full aspect-video object-cover" data-id="element-2039" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/50" data-id="element-2040">
                    <Play className="w-12 h-12 text-white fill-white" data-id="element-2041" />
                  </div>
                  <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 rounded-md flex items-center" data-id="element-2042">
                    <Clock className="w-3 h-3 mr-1 text-purple-400" data-id="element-2043" />
                    <span className="text-xs" data-id="element-2044">{video.duration}</span>
                  </div>
                  {video.category === 'Rehberlik' && <div className="absolute top-2 left-2 px-2 py-1 bg-purple-500/80 rounded-md" data-id="element-2045">
                      <span className="text-xs font-medium" data-id="element-2046">
                        Rehberlik Programı
                      </span>
                    </div>}
                </div>
                <h3 className="mt-2 font-medium text-sm line-clamp-1" data-id="element-2047">
                  {video.title}
                </h3>
                <p className="text-xs text-gray-400" data-id="element-2048">
                  {video.category === 'Rehberlik' ? 'Haftalık Program' : video.category}
                </p>
              </div>)}
            {getFilteredVideos().length === 0 && <div className="col-span-full text-center py-8 text-gray-400" data-id="element-2049">
                Bu kategoride henüz video bulunmamaktadır.
              </div>}
          </div>
        </div>
      </div>
      {/* Guidance Notes Section */}
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-2050">
        <div className="relative bg-black/90 rounded-xl p-4" data-id="element-2051">
          <div className="flex justify-between items-center mb-3" data-id="element-2052">
            <h2 className="text-xl font-semibold" data-id="element-2053">Rehberlik Notları</h2>
            <button onClick={() => setShowNotesLibrary(true)} className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1" data-id="element-2054">
              Tümünü Gör
              <ArrowRight className="w-4 h-4" data-id="element-2055" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2056">
            {guidanceNotes.map(note => {
            const Icon = note.icon;
            return <div key={note.id} onClick={() => setShowNotesLibrary(true)} className="relative p-[1px] rounded-lg overflow-hidden bg-gradient-to-r from-purple-600/70 to-indigo-600/70 hover:from-purple-600 hover:to-indigo-600 transition-colors cursor-pointer group" data-id="element-2057">
                  <div className="relative p-3 bg-black/90 rounded-lg h-full" data-id="element-2058">
                    <div className="flex flex-col h-full" data-id="element-2059">
                      <Icon className="w-6 h-6 text-purple-400 mb-2" data-id="element-2060" />
                      <h3 className="text-lg font-medium group-hover:text-white transition-colors" data-id="element-2061">
                        {note.title}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2" data-id="element-2062">
                        {note.description}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
      {/* Modals */}
      {showProgramRequestModal && <ProgramRequestModal onClose={() => setShowProgramRequestModal(false)} onSubmit={handleProgramRequestSubmit} data-id="element-2063" />}
      {showSupportMessageModal && <SupportMessageModal onClose={() => setShowSupportMessageModal(false)} onSubmit={handleSupportMessageSubmit} data-id="element-2064" />}
      {/* Toast Container */}
      <ToastContainer data-id="element-2065" />
    </div>;
}